import React, { CSSProperties, FC, ReactElement, useMemo } from "react";
import { createPortal } from "react-dom";
import { Separator } from "@src/lib/ui/Separator";
import Spacer from "@src/lib/ui/Spacer";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import {
  TabStripCount,
  TabStripScrollFrame,
  useTabStripScroll,
} from "@src/lib/ui/tabStrip";

export interface QueryTabsProps<K extends string> {
  tabs: K[];
  dividerAfterTab?: K;
  hiddenTabs?: K[];
  render: QueryTabRenderFns<K>;
  labels: QueryTabCopy<K>;
  disabledFns?: QueryTabDisabledFns<K>;
  counts?: QueryTabCounts<K>;
  style?: CSSProperties;
  tabsContainerStyle?: CSSProperties;
  queryParamKey?: string;
  defaultTab?: K;
  contentContainerStyle?: CSSProperties;
  removeVerticalPadding?: Partial<Record<K, boolean>>;
  /**
   * When set, the tab strip is portaled here (e.g. `#top-nav-subheader`) so it
   * sits in the shell; tab content stays in place below. When unset the strip
   * renders in place, in a border-bottom row that matches the shell subheader.
   */
  subheaderPortal?: HTMLElement | null;
}

/**
 * Tabs synced to a URL query param, with a render map for the panels: a thin
 * wrapper over `Tabs queryParam` (src/lib/ui/Tabs.tsx). The selected tab's
 * panel is a real tabpanel. A param that names no visible, enabled tab shows
 * `defaultTab` (else the first visible tab) without rewriting the URL.
 */
export function QueryTabs<K extends string>({
  tabs,
  dividerAfterTab,
  hiddenTabs,
  labels,
  disabledFns,
  render,
  style,
  tabsContainerStyle,
  queryParamKey = "tab",
  defaultTab,
  contentContainerStyle,
  counts,
  removeVerticalPadding,
  subheaderPortal,
}: QueryTabsProps<K>): ReactElement {
  const visibleTabs = useMemo(
    () =>
      !hiddenTabs ? tabs : tabs.filter((tab) => !hiddenTabs.includes(tab)),
    [tabs, hiddenTabs],
  );

  const enabledTabs = visibleTabs.filter(
    (tab) => !(disabledFns?.[tab]?.() ?? false),
  );

  const tabButtons = visibleTabs.map((tab) => (
    <React.Fragment key={tab}>
      <TabsTrigger
        value={tab}
        disabled={disabledFns?.[tab]?.() ?? false}
        data-testid={`tab-${tab}`}
        // The subheader strip's 4px label-to-count gap.
        className="gap-1"
        count={counts?.[tab]}
      >
        {labels[tab]}
      </TabsTrigger>
      {tab === dividerAfterTab && (
        <Separator
          orientation="vertical"
          className="mx-1 my-[9px] h-auto self-stretch bg-border-primary"
        />
      )}
    </React.Fragment>
  ));

  const tabStrip = subheaderPortal ? (
    <SubheaderTabsScroller style={tabsContainerStyle}>
      {tabButtons}
    </SubheaderTabsScroller>
  ) : (
    // Mirrors the shell subheader row (`#top-nav-subheader` in Page.tsx) so an
    // in-place strip is indistinguishable from a portaled one.
    <div className="flex min-h-10 w-full min-w-0 shrink-0 items-center border-b border-border-primary">
      <TabsList
        data-testid="subheader-tabs"
        className="hide-scrollbar h-full min-h-0 min-w-0 overflow-x-auto"
        style={tabsContainerStyle}
      >
        {tabButtons}
      </TabsList>
    </div>
  );

  return (
    <Tabs
      queryParam={queryParamKey}
      defaultValue={defaultTab ?? visibleTabs[0]}
      className="flex flex-1 flex-col self-stretch"
      style={style}
      data-testid={"tabs"}
    >
      {subheaderPortal ? createPortal(tabStrip, subheaderPortal) : tabStrip}
      <div
        className="flex flex-1 self-stretch"
        style={{
          overflowX: "hidden",
          ...contentContainerStyle,
        }}
      >
        {/* A disabled tab never shows, so it gets no panel (not even for the
            first commit, before Tabs validates the URL against the triggers). */}
        {enabledTabs.map((tab) => {
          const shouldRemoveVerticalPadding =
            removeVerticalPadding?.[tab] ?? false;
          return (
            <TabsContent
              key={tab}
              value={tab}
              asChild
              // The panel scrolls (overflow-y auto), so a radius would clip
              // its content. Not a Tab stop either: its focus ring would be
              // clipped by the content row's overflow, and main had no stop
              // here, so the Tab order stays as it was.
              className="rounded-none"
              tabIndex={-1}
            >
              <ScrollableFlexGrow
                alignSelf={"stretch"}
                flex={1}
                style={{
                  overflowY: "auto",
                  zIndex: 0,
                  position: "relative",
                }}
              >
                {shouldRemoveVerticalPadding ? null : <Spacer size={21} />}
                <QueryTabPanelContent content={render[tab]} />
                {shouldRemoveVerticalPadding ? null : <Spacer size={64} />}
              </ScrollableFlexGrow>
            </TabsContent>
          );
        })}
      </div>
    </Tabs>
  );
}

/** Calls a tab's render function only once its panel mounts. */
function QueryTabPanelContent({
  content,
}: {
  content: (() => React.ReactNode) | React.ReactNode;
}): ReactElement {
  return <>{typeof content === "function" ? content() : content}</>;
}

/**
 * The portaled strip: a horizontally scrolling TabsList that fills the rest of
 * the subheader row, with edge fades and page buttons once it overflows (the
 * shared scroller in tabStrip.tsx). The active underline sits on the
 * subheader row's divider even while the list scrolls.
 */
const SubheaderTabsScroller: FC<{
  children: React.ReactNode;
  style?: CSSProperties;
}> = ({ children, style }) => {
  const { scrollRef, canScrollLeft, canScrollRight, scrollByPage } =
    useTabStripScroll<HTMLDivElement>(children);

  return (
    <TabStripScrollFrame
      className="relative order-first flex h-full w-0 min-w-0 flex-1 items-center overflow-hidden"
      canScrollLeft={canScrollLeft}
      canScrollRight={canScrollRight}
      scrollByPage={scrollByPage}
    >
      <TabsList
        ref={scrollRef}
        data-testid="subheader-tabs"
        className="hide-scrollbar h-full min-h-0 w-full min-w-0 overflow-x-auto overflow-y-hidden"
        style={style}
      >
        {children}
      </TabsList>
    </TabStripScrollFrame>
  );
};

export type QueryTabRenderFns<K extends string> = Record<
  K,
  (() => React.ReactNode) | React.ReactNode
>;

export type QueryTabCopy<K extends string> = Record<K, string>;
export type QueryTabCounts<K extends string> = Partial<Record<K, number>>;

export type QueryTabDisabledFns<K extends string> = Record<K, () => boolean>;

/** The count chip after a tab label (the same chip as `TabsTrigger count`). */
export const QueryTabCount = TabStripCount;
